import moment from "moment";
import { ADMIN_ROLE, ALL_ROLES, APP_URL, statusToColors } from "./constants";
import {
  calculateNumberOfDays,
  calculateQuoteRequestReceivedTime,
} from "./dataSheetConstants";

export const DEFAULT_DATE_TIME_FORMATE = "YYYY MMMM DD, hh:mm a";
export const DEFAULT_DATE_FORMATE = "YYYY MMMM DD";
export const DEFAULT_TIME_FORMATE = "hh:mm a";
export const DEFAULT_YEAR_FORMAT = "YYYY";

export const isAdmin = (role) => role === ADMIN_ROLE;

export const dateMinusToday = (date) => {
  if (!date) return "N/A";

  const closingDate = new Date(date);
  closingDate.setHours(0);
  closingDate.setMinutes(0);
  closingDate.setSeconds(0);
  closingDate.setMilliseconds(0);

  const diffTime = closingDate.getTime() - new Date().getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const dateMinusDate = (date1, date2) => {
  if (!date1 || !date2) return "N/A";

  const date1Date = new Date(date1);
  date1Date.setHours(0);
  date1Date.setMinutes(0);
  date1Date.setSeconds(0);
  date1Date.setMilliseconds(0);

  const date2Date = new Date(date2);
  date2Date.setHours(0);
  date2Date.setMinutes(0);
  date2Date.setSeconds(0);
  date2Date.setMilliseconds(0);

  const diffTime = date1Date.getTime() - date2Date.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const fillJobVariablesInText = (text, job) => {
  let newText = text;
  const availableHeaders = Object.keys(job || {});
  availableHeaders.forEach((h) => {
    newText = newText.replace(`{{${h}}}`, job[h]);
  });
  return newText;
};

export const getValidUrl = (link) => {
  if (!link.startsWith("https://") && !link.startsWith("http://")) {
    link = "https://" + link;
  }

  return link;
};

export const createMapsLinkFromJobs = (
  jobs,
  zoom,
  center,
  radius,
  showCircle
) => {
  // Ensure APP_URL is defined and valid
  if (!APP_URL || !APP_URL.startsWith("http")) {
    throw new Error("Invalid APP_URL");
  }

  const jobsWithLocation = jobs.filter((j) => {
    return (
      j["latitude"] &&
      j["longitude"] &&
      !isNaN(Number(j["latitude"])) &&
      !isNaN(Number(j["longitude"]))
    );
  });

  const centerXY =
    center && center.latitude && center.longitude
      ? `${center.latitude}:${center.longitude}`
      : "";

  const url = `${APP_URL}/jobs/map?zoom=${zoom}&center=${centerXY}&radius=${radius}&circle=${
    showCircle ? "true" : ""
  }&m=${encodeURIComponent(
    jobsWithLocation
      .map(
        (j) =>
          `${j["latitude"]}:${j["longitude"]}:${trimID(j["atmId"])}-${j[
            "propertyAddress"
          ] || ""} ${j["propertyStreet"] || ""}${
            j["acreage"] ? ` (${j["acreage"]})` : ""
          }`
      )
      .join(",")
  )}`;

  return url;
};

export const getColumnColor = (job = {}, field, location) => {
  const columnsToCheck = [
    "Longitude",
    "Latitude",
    "Property Appraisal ID",
    "Acreage",
    "Waiting For Quote",
    "Folder",
    "Appr",
    "Appr Map",
    "Appr Image",
    "Google Map",
    "View",
  ];
  if (!columnsToCheck.includes(field.label)) return null;

  if (
    field.label === "Acreage" &&
    job["typeOfLandSurvey"] === "Acreage" &&
    !job[(field?.key)]
  ) {
    return "red";
  }
  if (
    field.label === "Folder" ||
    field.label === "Google Map" ||
    field.label === "View"
  ) {
    return "info";
  }
  if (field.label === "Appr" || field.label === "Appr Map") {
    return job["propertyAppraisalId"]?.length > 6 ? "danger" : "info";
  }
  if (field.label === "Appr Image") {
    return job["appraisalImageId"] ? "success" : "info";
  }
  if (
    field.label === "propertyAppraisalId" &&
    job["propertyAppraisalId"] &&
    job["propertyAppraisalId"].length > 6
  ) {
    return "red";
  }
  if (field.label === "Longitude" || field.label === "Latitude") {
    if (!job["longitude"] || !job["latitude"]) return "red";
  }

  if (
    location.pathname.includes("quoting") &&
    field.label === "Waiting For Quote"
  ) {
    const value = calculateQuoteRequestReceivedTime(job["Submission Date"]);
    if (value > 48) {
      return "red";
    }
  }
};

export const getRowColor = (job, location, status) => {
  if (
    !status ||
    ["Current", "Drafting", "Reviewing", "Quoting"].includes(status)
  ) {
    if (
      location.pathname.includes("drafting") ||
      location.pathname.includes("reviewing")
    ) {
      const value = calculateNumberOfDays(
        new Date().toISOString(),
        job["titleClosingDate"]
      );
      if (!(value === null || value === undefined))
        return value < 4 ? "red" : "green";
      else return "";
    }

    if (location.pathname.includes("quoting")) {
      return job["value"] ? "#0080009c" : "";
    }

    return statusToColors[job["status"]];
  }
};

export const groupEntityTableValues = (
  entityData = [],
  groupBy,
  groupValues
) => {
  let finalEntityData = [];

  const otherData = entityData.filter(
    (ed) => !ed?.hasOwnProperty(groupBy) || !groupValues.includes(ed[groupBy])
  );

  groupValues.forEach((groupValue) => {
    const filteredEntityData = entityData.filter(
      (ed) => ed[groupBy] === groupValue
    );
    filteredEntityData.forEach((d) => {
      delete d?.newGroupStarts;
    });
    if (filteredEntityData.length) {
      filteredEntityData[0] = {
        ...filteredEntityData[0],
        newGroupStarts: true,
      };

      finalEntityData.push(...filteredEntityData);
    }
  });

  if (otherData?.length) {
    otherData.forEach((ot) => {
      delete ot.newGroupStarts;
    });
    otherData[0].newGroupStarts = true;
    otherData.forEach((od) => {
      od[groupBy] = "Others";
    });
  }
  finalEntityData = [...finalEntityData, ...otherData];

  return finalEntityData;
};

const SESSION_AUTH_TOKEN_KEY = "auth-token";

export const getSessionAuthToken = () => {
  try {
    return sessionStorage.getItem(SESSION_AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setSessionAuthToken = (token) => {
  try {
    if (token) {
      sessionStorage.setItem(SESSION_AUTH_TOKEN_KEY, token);
    } else {
      sessionStorage.removeItem(SESSION_AUTH_TOKEN_KEY);
    }
  } catch {
    // Ignore sessionStorage failures (private mode quirks, etc.)
  }
};

export const clearSessionAuthToken = () => {
  setSessionAuthToken(null);
};

/** Clears legacy localStorage user blob from older auth flow. */
export const clearLegacyStoredUser = () => {
  try {
    localStorage.removeItem("user");
  } catch {
    // ignore
  }
};

export function combineDateAndTime(date, time) {
  if (!date) date = moment(new Date()).format("YYYY-MM-DD");
  if (!time) time = "00:00";
  return new Date(`${date}T${time}:00`);
}

export const updateItemsInArray = (originalData, updatedData) => {
  const returnData = [...originalData];

  if (!Array.isArray(updatedData)) {
    updatedData = [updatedData];
  }

  updatedData.forEach((newData) => {
    const existingIndex = returnData.findIndex((d) => d._id === newData._id);
    if (existingIndex !== -1) {
      returnData[existingIndex] = newData;
    } else {
      returnData.unshift(newData);
    }
  });

  return returnData;
};

export const getAppChoicesFromKey = (appChoices = [], appChoiceKey) => {
  let result = [];
  if (appChoices) {
    if (ALL_ROLES.includes(appChoiceKey)) {
      result =
        appChoices?.find((appChoice) => appChoice?.key === "users")?.values ||
        [];
      return [
        { name: "Unassigned" },
        ...result
          ?.filter((u) => u?.role === appChoiceKey)
          .map((u) => ({
            ...u,
            name: `${u.crew ? `${u.crew}: ` : ``}${u.name} `,
            value: u._id,
          })),
      ];
    }

    if (appChoiceKey === "users") {
      return (
        appChoices?.find((appChoice) => appChoice?.key === appChoiceKey)
          ?.values || []
      );
    }

    result =
      appChoices
        ?.find((appChoice) => appChoice?.key === appChoiceKey)
        ?.values.map((value) =>
          typeof value === "object"
            ? value?.county || value?.name || value?.value
            : value
        ) || [];

    return result;
  }
};

export const requestFilterInstance = (filters) => {
  return filters.reduce((allFilters, currFilter) => {
    return {
      ...allFilters,
      [currFilter?.key]: currFilter?.children?.length
        ? currFilter.children.flatMap((c) =>
            c?.key === null ? [null, ""] : [c?.key]
          )
        : undefined,
    };
  }, {});
};

export function trimID(id) {
  // This regular expression matches 'ATM' followed by any number of zeros and then captures the remaining numbers
  const pattern = /^ATM0*(\d+)$/;
  const match = id.match(pattern);

  if (match && match[1]) {
    return match[1];
  } else {
    return id;
  }
}

export const formatTime = (milliseconds) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
    2,
    "0"
  );
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${hours} : ${minutes} : ${seconds}`;
};

export const extractJobIdFromPathname = (pathname) => {
  const jobRoutePattern = /^\/job\/([a-f0-9]{24})$/;

  const match = pathname.match(jobRoutePattern);
  if (match && match[1]) {
    return match[1];
  }

  return null;
};

export function getDuration({ startDateTime, endDateTime = new Date() }) {
  // Parse the input dates
  const start = new Date(startDateTime);
  const end = new Date(endDateTime);

  // Calculate the difference in milliseconds
  const diffInMs = end - start;

  // Convert the difference to hours and minutes
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInMinutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));

  // Format the output based on the duration
  if (diffInHours > 0) {
    return `${diffInHours} hr ${diffInMinutes} min`;
  } else {
    return `${diffInMinutes} min`;
  }
}

export function calculateDateTimeRange(startDT, endDT, rangeType, direction) {
  const startDateTime = new Date(startDT);
  const endDateTime = new Date(endDT);

  const oneDay = 24 * 60 * 60 * 1000; // Milliseconds in one day

  let newStartDateTime, newEndDateTime;

  switch (rangeType) {
    case "Day":
      if (direction === "Previous") {
        newStartDateTime = new Date(startDateTime.getTime() - oneDay);
        newEndDateTime = new Date(endDateTime.getTime() - oneDay);
      } else if (direction === "Next") {
        newStartDateTime = new Date(startDateTime.getTime() + oneDay);
        newEndDateTime = new Date(endDateTime.getTime() + oneDay);
      }
      break;

    case "Week":
      if (direction === "Previous") {
        newStartDateTime = new Date(startDateTime.getTime() - 7 * oneDay);
        newEndDateTime = new Date(endDateTime.getTime() - 7 * oneDay);
      } else if (direction === "Next") {
        newStartDateTime = new Date(startDateTime.getTime() + 7 * oneDay);
        newEndDateTime = new Date(endDateTime.getTime() + 7 * oneDay);
      }
      break;

    case "Month":
      let newYear = startDateTime.getFullYear();
      let newMonth = startDateTime.getMonth();

      if (direction === "Previous") {
        newMonth--;
        if (newMonth === 0) {
          newMonth = 12;
          newYear--;
        }
      } else if (direction === "Next") {
        newMonth++;
        if (newMonth === 13) {
          newMonth = 1;
          newYear++;
        }
      }

      // Create the first and last dates of the new month
      newStartDateTime = new Date(newYear, newMonth, 1);
      newEndDateTime = new Date(newYear, newMonth + 1, 0); // The last day of the month
      break;

    case "Quarter":
      if (direction === "Previous") {
        newStartDateTime = new Date(
          startDateTime.setMonth(startDateTime.getMonth() - 3)
        );
        newEndDateTime = new Date(
          endDateTime.setMonth(endDateTime.getMonth() - 3)
        );
      } else if (direction === "Next") {
        newStartDateTime = new Date(
          startDateTime.setMonth(startDateTime.getMonth() + 3)
        );
        newEndDateTime = new Date(
          endDateTime.setMonth(endDateTime.getMonth() + 3)
        );
      }
      break;

    case "Year":
      if (direction === "Previous") {
        newStartDateTime = new Date(
          startDateTime.setFullYear(startDateTime.getFullYear() - 1)
        );
        newEndDateTime = new Date(
          endDateTime.setFullYear(endDateTime.getFullYear() - 1)
        );
      } else if (direction === "Next") {
        newStartDateTime = new Date(
          startDateTime.setFullYear(startDateTime.getFullYear() + 1)
        );
        newEndDateTime = new Date(
          endDateTime.setFullYear(endDateTime.getFullYear() + 1)
        );
      }
      break;

    default:
      throw new Error("Invalid range type");
  }

  newStartDateTime.setHours(0, 0, 0, 0);
  newEndDateTime.setHours(23, 59, 59, 999);

  return {
    startDateTime: newStartDateTime,
    endDateTime: newEndDateTime,
  };
}

export const getSortedCountyValues = (appChoices) => {
  const counties = getAppChoicesFromKey(appChoices, "County");

  const brazosIndex = counties.indexOf("Brazos");
  const sorted = [...counties];

  if (brazosIndex !== -1) {
    const [brazos] = sorted.splice(brazosIndex, 1);
    sorted.sort((countyA, countyB) => countyA.localeCompare(countyB));
    sorted.unshift(brazos);
  } else {
    sorted.sort((countyA, countyB) => countyA.localeCompare(countyB));
  }

  return sorted;
};

// Returns the label of the static range containing the start and end dates, or null if none applies.

export function giveStaticRangeLabel(start, end, presets) {
  if (!start || !end) return false;

  let currentStart = moment(start).startOf("day");
  let currentEnd = moment(end).endOf("day");

  for (let label in presets) {
    if (!presets.hasOwnProperty(label)) continue;
    let p = presets[label];

    let presetStart = moment(p.startDate).startOf("day");
    let presetEnd = moment(p.endDate).endOf("day");

    if (currentStart.isSame(presetStart) && currentEnd.isSame(presetEnd)) {
      return label;
    }
  }

  return null;
}
